import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { PERMISSIONS, WAYPOINT_TYPES, WaypointSchema, latToDms, lonToDms, type WaypointInput } from '@jupi/shared'
import { ApiError, useAuth, useSnackbar } from '@jupi/ui'
import { useCountries, useCreateWaypoint, useUpdateWaypoint, type PlanWaypoint } from '../../api/plan'
import { countryName } from './WaypointsPage'

const TEXT_FIELDS = ['name', 'com', 'gnd', 'atis', 'vor', 'area', 'link'] as const
type Form = Record<'code' | 'lat' | 'lon' | 'variation' | 'country' | 'waypointType' | (typeof TEXT_FIELDS)[number], string>

const toForm = (w: PlanWaypoint | null): Form => ({
  code: w?.code ?? '',
  lat: w ? String(w.lat) : '',
  lon: w ? String(w.lon) : '',
  variation: w?.variation != null ? String(w.variation) : '',
  country: w?.country ?? 'fi',
  waypointType: w?.waypointType ?? 'udef',
  name: w?.name ?? '',
  com: w?.com ?? '',
  gnd: w?.gnd ?? '',
  atis: w?.atis ?? '',
  vor: w?.vor ?? '',
  area: w?.area ?? '',
  link: w?.link ?? '',
})

const decimal = (value: string) => (value.trim() === '' ? null : Number(value.replace(',', '.')))

// Creating or editing a waypoint. A member's new point is theirs alone
// until fpl.admin shares it. "Use my position" fills in where the pilot is
// standing -- the quickest way to add a strip that is on no chart.
export default function WaypointDialog({
  open,
  waypoint,
  onClose,
}: {
  open: boolean
  /** null: a new waypoint. */
  waypoint: PlanWaypoint | null
  onClose: () => void
}) {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { showSnackbar } = useSnackbar()
  const { can } = useAuth()
  // An aerodrome is club reference data: only fpl.admin makes one (the API holds the same line).
  const types = WAYPOINT_TYPES.filter((type) => type !== 'ad' || can(PERMISSIONS.fplAdmin) || waypoint?.waypointType === 'ad')
  const { data: countries = [] } = useCountries()
  const create = useCreateWaypoint()
  const update = useUpdateWaypoint()
  const [form, setForm] = useState<Form>(() => toForm(waypoint))

  useEffect(() => {
    if (open) setForm(toForm(waypoint))
  }, [open, waypoint])

  const set = (key: keyof Form, value: string) => setForm((f) => ({ ...f, [key]: value }))
  const parsed = WaypointSchema.safeParse({
    ...Object.fromEntries(TEXT_FIELDS.map((key) => [key, form[key].trim() || null])),
    code: form.code,
    lat: decimal(form.lat),
    lon: decimal(form.lon),
    variation: decimal(form.variation),
    country: form.country,
    waypointType: form.waypointType,
  })
  const input: WaypointInput | null = parsed.success ? parsed.data : null
  const lat = decimal(form.lat)
  const lon = decimal(form.lon)

  const here = () =>
    navigator.geolocation?.getCurrentPosition(
      (pos) => {
        set('lat', pos.coords.latitude.toFixed(5))
        set('lon', pos.coords.longitude.toFixed(5))
      },
      () => showSnackbar(t('plan.waypoints.locationDenied'), { severity: 'warning' }),
      { enableHighAccuracy: true, timeout: 15_000 },
    )

  const save = async () => {
    if (!input) return
    try {
      if (waypoint) await update.mutateAsync({ ...input, code: waypoint.code, newCode: input.code })
      else await create.mutateAsync(input)
      showSnackbar(t('plan.waypoints.saved'))
      onClose()
    } catch (err) {
      const taken = err instanceof ApiError && err.status === 409
      showSnackbar(taken ? t('plan.waypoints.codeTaken') : t('common.error'), { severity: 'error' })
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} fullWidth maxWidth="sm">
      <DialogTitle>{waypoint ? t('plan.waypoints.edit') : t('plan.waypoints.new')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr' } }}>
            <TextField
              required
              label={t('plan.waypoints.code')}
              value={form.code}
              onChange={(e) => set('code', e.target.value.toUpperCase())}
              slotProps={{ htmlInput: { autoCapitalize: 'characters', maxLength: 20 } }}
            />
            <TextField label={t('plan.waypoints.name')} value={form.name} onChange={(e) => set('name', e.target.value)} />
          </Box>
          <TextField select label={t('plan.waypoints.type')} value={form.waypointType} onChange={(e) => set('waypointType', e.target.value)}>
            {types.map((type) => (
              <MenuItem key={type} value={type}>
                {t(`plan.waypoints.type_${type}`)}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <TextField
              required
              label={t('plan.waypoints.lat')}
              value={form.lat}
              onChange={(e) => set('lat', e.target.value)}
              helperText={lat !== null && Number.isFinite(lat) && Math.abs(lat) <= 90 ? latToDms(lat) : ' '}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            />
            <TextField
              required
              label={t('plan.waypoints.lon')}
              value={form.lon}
              onChange={(e) => set('lon', e.target.value)}
              helperText={lon !== null && Number.isFinite(lon) && Math.abs(lon) <= 180 ? lonToDms(lon) : ' '}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            />
          </Box>
          <Box>
            <Button startIcon={<Icon icon="mdi:satellite-variant" />} onClick={here}>
              {t('plan.waypoints.useMyPosition')}
            </Button>
          </Box>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' } }}>
            <TextField
              select
              label={t('plan.waypoints.country')}
              value={form.country}
              onChange={(e) => set('country', e.target.value)}
            >
              {countries.map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  {countryName(c, i18n.language)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={`${t('plan.waypoints.variation')} (°E)`}
              value={form.variation}
              onChange={(e) => set('variation', e.target.value)}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            />
            {(['com', 'gnd', 'atis', 'vor'] as const).map((key) => (
              <TextField key={key} label={t(`plan.waypoints.${key}`)} value={form[key]} onChange={(e) => set(key, e.target.value)} />
            ))}
          </Box>
          <TextField label={t('plan.waypoints.area')} value={form.area} onChange={(e) => set('area', e.target.value)} />
          <TextField
            label={t('plan.waypoints.link')}
            value={form.link}
            placeholder="https://"
            onChange={(e) => set('link', e.target.value)}
            slotProps={{ htmlInput: { inputMode: 'url' } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={!input || create.isPending || update.isPending} onClick={() => void save()}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
